import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  FormatListNumbered as FormatListNumberedIcon,
  SwapVert as SwapVertIcon,
  VerticalAlignTop as VerticalAlignTopIcon,
} from "@mui/icons-material";
import { TextField, Tooltip } from "@mui/material";
import { MenuButton, MenuItemWithIcon, Modal, ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  moveAvailability,
  PREFERENCE_REORDER_LIMIT,
  PreferenceMove,
} from "../productPreferences.js";

interface Props {
  /** What the item names, so the control's label says what it moves. */
  name: string;
  /** 0-based position within the group. */
  index: number;
  groupSize: number;
  /** False for an organisation item without `pim_catalogue_update`. */
  mayWrite: boolean;
  disabled: boolean;
  onMove: (move: PreferenceMove) => void;
}

/** Moves one item within its group: the same kind at the same scope. */
export function PreferenceMoveButton({
  name,
  index,
  groupSize,
  mayWrite,
  disabled,
  onMove,
}: Props) {
  const { t } = useTranslate(["Product", "Global"]);
  const available = moveAvailability(index, groupSize, mayWrite);

  const label = `${name}: ${t("Move", { ns: "Global" })}`;
  const items = [
    <MenuItemWithIcon
      key="top"
      icon={<VerticalAlignTopIcon />}
      disabled={!available.top}
      onClick={() => onMove({ kind: "top" })}
    >
      {t("Move to top", { ns: "Product" })}
    </MenuItemWithIcon>,
    <MenuItemWithIcon
      key="up"
      icon={<ArrowUpwardIcon />}
      disabled={!available.up}
      onClick={() => onMove({ kind: "up" })}
    >
      {t("Move up", { ns: "Product" })}
    </MenuItemWithIcon>,
    <MenuItemWithIcon
      key="down"
      icon={<ArrowDownwardIcon />}
      disabled={!available.down}
      onClick={() => onMove({ kind: "down" })}
    >
      {t("Move down", { ns: "Product" })}
    </MenuItemWithIcon>,
    <ModalOpenButton
      key="position"
      Modal={MoveToPositionModal}
      modalProps={{
        index,
        groupSize,
        onMove: position => onMove({ kind: "position", position }),
      }}
      disabled={!available.position}
    >
      <MenuItemWithIcon
        icon={<FormatListNumberedIcon />}
        disabled={!available.position}
      >
        {t("Move to position…", { ns: "Product" })}
      </MenuItemWithIcon>
    </ModalOpenButton>,
  ];

  return (
    <MenuButton
      Icon={<SwapVertIcon fontSize="small" />}
      tooltip={label}
      buttonProps={{
        size: "small",
        color: "primary",
        disabled,
        "aria-label": label,
      }}
    >
      {mayWrite
        ? items
        : items.map(item => (
            <Tooltip key={item.key} title={organisationPermissionReason(t)}>
              <div>{item}</div>
            </Tooltip>
          ))}
    </MenuButton>
  );
}

export function organisationPermissionReason(
  t: ReturnType<typeof useTranslate>["t"]
): string {
  return t(
    "You need permission to manage the product catalogue to change your organisation's preferences.",
    { ns: "Product" }
  );
}

function MoveToPositionModal({
  index,
  groupSize,
  onMove,
  handleClose,
}: {
  index: number;
  groupSize: number;
  onMove: (position: number) => void;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["Product", "Global"]);
  const [value, setValue] = React.useState(String(index + 1));

  const maxPosition = Math.min(groupSize, PREFERENCE_REORDER_LIMIT);
  const position = Number(value);
  const valid =
    Number.isInteger(position) &&
    position >= 1 &&
    position <= maxPosition &&
    position !== index + 1;

  return (
    <Modal
      title={t("Move to position…", { ns: "Product" })}
      maxWidth="xs"
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Move", { ns: "Global" }),
          handleClick: () => {
            onMove(position);
            handleClose();
          },
          buttonProps: { disabled: !valid },
        },
      ]}
    >
      <TextField
        type="number"
        label={t("Position", { ns: "Global" })}
        value={value}
        onChange={event => setValue(event.target.value)}
        helperText={t("Between 1 and {groupSize}", {
          ns: "Product",
          groupSize: maxPosition,
        })}
        slotProps={{ htmlInput: { min: 1, max: maxPosition } }}
        autoFocus
        fullWidth
      />
    </Modal>
  );
}
